import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { UserPlus, ShieldCheck, KeyRound, Users, Pencil, Crown } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { InlineConfirm } from '@/components/ui/InlineConfirm';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { adminApi, rolesApi, rolePermissionsApi } from '@/lib/adminApi';
import type { AdminUserRow, CustomRoleRow, RolePermissionRow } from '@/lib/adminApi';
import { usePagePermissions } from '@/contexts/PagePermissionsContext';
import { rxkPageKeys, rxkPageLabels, rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

type Tab = 'users' | 'permissions' | 'roles';

const TONE_OPTIONS = ['primary', 'success', 'warning', 'error', 'info', 'neutral'] as const;

export default function AdminTeamPage() {
  const [tab, setTab] = useState<Tab>('users');
  const { isFounder } = usePagePermissions();
  const { push } = useToast();

  const [roles, setRoles] = useState<CustomRoleRow[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      setRoles(await rolesApi.list());
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  // Fetched once here (not inside AdminUsersTab) so RolesTab can also see
  // real per-role assignment counts — adminApi.listUsers() is the only
  // client-reachable source for "every user_roles row" at all, since
  // user_roles itself only has a self-read RLS policy (the edge function
  // uses the service role internally to bypass that for role='admin' callers).
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const { users: rows } = await adminApi.listUsers();
      setUsers(rows);
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to load team members');
    } finally {
      setUsersLoading(false);
    }
  }, [push]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const roleLabel = (key: string) => roles.find((r) => r.key === key)?.label ?? key;
  const roleTone = (key: string) => (roles.find((r) => r.key === key)?.tone ?? 'neutral') as 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  const assignedCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const u of users) counts[u.role] = (counts[u.role] ?? 0) + 1;
    return counts;
  }, [users]);

  return (
      <div className="space-y-5">
        <div>
          <h1 className="text-[18px] font-bold text-ink">Team</h1>
          <p className="text-[12px] text-ink-faint mt-0.5">Who can access this admin panel, and which pages each role can see.</p>
        </div>

        <div className="flex items-center gap-1.5">
          {([
            { key: 'users' as const, label: 'Admin Users', icon: Users },
            { key: 'permissions' as const, label: 'Role Permissions', icon: ShieldCheck },
            { key: 'roles' as const, label: 'Roles', icon: KeyRound },
          ]).map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 text-[12.5px] font-semibold px-3.5 py-2 rounded-full cursor-pointer transition-colors whitespace-nowrap ${
                  active ? 'bg-primary-800 text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {rolesLoading ? (
          <Spinner />
        ) : tab === 'users' ? (
          <AdminUsersTab
            roles={roles}
            roleLabel={roleLabel}
            roleTone={roleTone}
            users={users}
            loading={usersLoading}
            onChanged={loadUsers}
          />
        ) : tab === 'permissions' ? (
          <RolePermissionsTab roles={roles} roleTone={roleTone} />
        ) : (
          <RolesTab roles={roles} isFounder={isFounder} onChanged={loadRoles} assignedCounts={assignedCounts} />
        )}
      </div>
  );
}

// ───────────────────────── Admin Users ─────────────────────────

function AdminUsersTab({
  roles,
  roleLabel,
  roleTone,
  users,
  loading,
  onChanged,
}: {
  roles: CustomRoleRow[];
  roleLabel: (key: string) => string;
  roleTone: (key: string) => 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  users: AdminUserRow[];
  loading: boolean;
  onChanged: () => void;
}) {
  const { push } = useToast();
  const [founderIds, setFounderIds] = useState<Set<string>>(new Set());
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', full_name: '', role: '' });
  const [editingUser, setEditingUser] = useState<AdminUserRow | null>(null);
  const [editRole, setEditRole] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // founder_access is readable directly (RLS allows it for role='admin'
    // callers) — just for the "Master" badge, doesn't need the edge function.
    supabase
      .from('founder_access')
      .select('user_id')
      .then(({ data }) => setFounderIds(new Set(((data as { user_id: string }[]) ?? []).map((r) => r.user_id))));
  }, []);

  const openInvite = () => {
    setInviteForm({ email: '', full_name: '', role: roles.find((r) => !r.is_system)?.key ?? roles[0]?.key ?? '' });
    setInviteOpen(true);
  };

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.inviteUser(inviteForm.email, inviteForm.role, inviteForm.full_name);
      push('success', `Invite sent to ${inviteForm.email}`);
      setInviteOpen(false);
      onChanged();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setBusy(false);
    }
  };

  const openEditRole = (u: AdminUserRow) => {
    setEditingUser(u);
    setEditRole(u.role);
  };

  const handleUpdateRole = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setBusy(true);
    try {
      await adminApi.updateRole(editingUser.user_id, editRole);
      push('success', 'Role updated');
      setEditingUser(null);
      onChanged();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (u: AdminUserRow) => {
    try {
      await adminApi.removeUser(u.user_id);
      push('success', `${u.email} removed`);
      onChanged();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to remove user');
    }
  };

  return (
    <Card>
      <CardHeader
        title="Admin Users"
        subtitle={`${users.length} total`}
        icon={<Users className="w-4 h-4" />}
        action={
          <Button size="sm" onClick={openInvite}>
            <UserPlus className="w-3.5 h-3.5" /> Assign New Admin
          </Button>
        }
      />
      <CardBody className="space-y-1">
        {loading ? (
          <Spinner />
        ) : users.length === 0 ? (
          <EmptyState title="No team members yet" />
        ) : (
          users.map((u) => {
            const master = founderIds.has(u.user_id);
            return (
              <div key={u.user_id} className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-surface-muted">
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-700 to-primary-900 text-white flex items-center justify-center text-[12px] font-bold flex-shrink-0">
                    {(u.full_name || u.email).slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-ink truncate">{u.full_name || u.email}</p>
                  <p className="text-[11.5px] text-ink-faint truncate">{u.email}</p>
                </div>
                <Badge tone={roleTone(u.role)}>{roleLabel(u.role)}</Badge>
                <span className="text-[11px] text-ink-faint w-16 flex-shrink-0 flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${u.confirmed ? 'bg-success' : 'bg-warning'}`} />
                  {u.confirmed ? 'Active' : 'Invited'}
                </span>
                <span className="text-[11px] text-ink-faint w-24 flex-shrink-0">{new Date(u.created_at).toLocaleDateString()}</span>
                {master ? (
                  <Badge tone="primary">
                    <Crown className="w-3 h-3 mr-1 inline" /> Master
                  </Badge>
                ) : (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button size="sm" variant="secondary" onClick={() => openEditRole(u)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <InlineConfirm onConfirm={() => handleRemove(u)} label="" ariaLabel="Remove user" />
                  </div>
                )}
              </div>
            );
          })
        )}
      </CardBody>

      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Assign New Admin"
        icon={<UserPlus className="w-3.5 h-3.5" />}
        busy={busy}
        footer={
          <>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setInviteOpen(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" form="invite-admin-form" className="flex-1" loading={busy}>Send Invite</Button>
          </>
        }
      >
        <form id="invite-admin-form" onSubmit={handleInvite} className="space-y-3.5">
          <Field label="Email">
            <Input type="email" required value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} />
          </Field>
          <Field label="Full Name">
            <Input required value={inviteForm.full_name} onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })} />
          </Field>
          <Field label="Role">
            <Select required value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}>
              {roles.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </Select>
          </Field>
          <p className="text-[11px] text-ink-faint">They'll get an email invite to set their password. Which pages they can see depends on the role's permissions (Role Permissions tab).</p>
        </form>
      </Modal>

      <Modal
        open={!!editingUser}
        onClose={() => setEditingUser(null)}
        title="Change Role"
        subtitle={editingUser?.email}
        icon={<Pencil className="w-3.5 h-3.5" />}
        busy={busy}
        footer={
          <>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setEditingUser(null)} disabled={busy}>Cancel</Button>
            <Button type="submit" form="edit-role-form" className="flex-1" loading={busy}>Save</Button>
          </>
        }
      >
        <form id="edit-role-form" onSubmit={handleUpdateRole} className="space-y-3.5">
          <Field label="Role">
            <Select required value={editRole} onChange={(e) => setEditRole(e.target.value)}>
              {roles.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </Select>
          </Field>
        </form>
      </Modal>
    </Card>
  );
}

// ───────────────────────── Role Permissions ─────────────────────────

function RolePermissionsTab({
  roles,
  roleTone,
}: {
  roles: CustomRoleRow[];
  roleTone: (key: string) => 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
}) {
  const { push } = useToast();
  const [perms, setPerms] = useState<RolePermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPerms(await rolePermissionsApi.list());
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [push]);

  useEffect(() => {
    load();
  }, [load]);

  const has = (role: string, pageKey: string) => perms.some((p) => p.role === role && p.page_key === pageKey);

  const toggle = async (role: string, pageKey: string) => {
    const cellKey = `${role}:${pageKey}`;
    setPending((p) => new Set(p).add(cellKey));
    const currentlyGranted = has(role, pageKey);
    try {
      if (currentlyGranted) {
        setPerms((p) => p.filter((r) => !(r.role === role && r.page_key === pageKey)));
        await rolePermissionsApi.revoke(role, pageKey);
      } else {
        setPerms((p) => [...p, { role, page_key: pageKey }]);
        await rolePermissionsApi.grant(role, pageKey);
      }
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save — reverting');
      await load();
    } finally {
      setPending((p) => {
        const next = new Set(p);
        next.delete(cellKey);
        return next;
      });
    }
  };

  if (loading) return <Spinner />;

  return (
    <Card>
      <CardHeader title="Role Permissions" subtitle="Which sidebar pages each role can see in this admin panel" icon={<ShieldCheck className="w-4 h-4" />} />
      <CardBody className="overflow-x-auto">
        <p className="text-[11px] text-ink-faint mb-3">Founder (Master) accounts always have full access regardless of this grid.</p>
        <table className="w-full border-collapse min-w-[600px]">
          <thead>
            <tr>
              <th className="text-left text-[10.5px] tracking-[0.08em] uppercase font-semibold text-ink-faint py-2 pr-3 sticky left-0 bg-white">Page</th>
              {roles.map((r) => (
                <th key={r.key} className="text-center py-2 px-2">
                  <Badge tone={roleTone(r.key)}>{r.label}</Badge>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rxkPageKeys.map((pageKey) => (
              <tr key={pageKey} className="border-t border-border-subtle">
                <td className="text-[12.5px] font-medium text-ink py-2 pr-3 sticky left-0 bg-white whitespace-nowrap">{rxkPageLabels[pageKey]}</td>
                {roles.map((r) => {
                  const cellKey = `${r.key}:${pageKey}`;
                  const checked = has(r.key, pageKey);
                  const isPending = pending.has(cellKey);
                  return (
                    <td key={r.key} className="text-center py-2 px-2">
                      <button
                        type="button"
                        onClick={() => toggle(r.key, pageKey)}
                        disabled={isPending}
                        aria-pressed={checked}
                        aria-label={`${checked ? 'Revoke' : 'Grant'} ${rxkPageLabels[pageKey]} for ${r.label}`}
                        className={`w-5 h-5 rounded-md border cursor-pointer transition-colors disabled:opacity-50 ${
                          checked ? 'bg-primary-800 border-primary-800' : 'bg-white border-border hover:border-primary-300'
                        }`}
                      >
                        {checked && <span className="block w-full h-full text-white text-[12px] leading-[18px]">✓</span>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}

// ───────────────────────── Roles ─────────────────────────

function RolesTab({
  roles,
  isFounder,
  onChanged,
  assignedCounts,
}: {
  roles: CustomRoleRow[];
  isFounder: boolean;
  onChanged: () => void;
  assignedCounts: Record<string, number>;
}) {
  const { push } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ key: '', label: '', tone: 'neutral' as (typeof TONE_OPTIONS)[number] });
  const [busy, setBusy] = useState(false);

  const openCreate = () => {
    setForm({ key: '', label: '', tone: 'neutral' });
    setModalOpen(true);
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const key = form.key.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
      if (!key) throw new Error('Role key is required');
      await rolesApi.create(key, form.label.trim(), form.tone);
      push('success', `Role "${form.label}" created`);
      setModalOpen(false);
      onChanged();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to create role');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (key: string) => {
    try {
      await rolesApi.remove(key);
      push('success', 'Role deleted');
      onChanged();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to delete — it may still be assigned to someone');
    }
  };

  return (
    <Card>
      <CardHeader
        title="Roles"
        subtitle={`${roles.length} total`}
        icon={<KeyRound className="w-4 h-4" />}
        action={
          isFounder ? (
            <Button size="sm" onClick={openCreate}>
              <UserPlus className="w-3.5 h-3.5" /> New Role
            </Button>
          ) : (
            <span className="text-[11px] text-ink-faint">Only the Founder account can create roles</span>
          )
        }
      />
      <CardBody className="space-y-1">
        {roles.map((r) => (
          <div key={r.key} className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-surface-muted">
            <Badge tone={r.tone as 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral'}>{r.label}</Badge>
            <span className="text-[11px] font-mono text-ink-disabled">{r.key}</span>
            <span className="flex-1" />
            {r.is_system && <span className="text-[10.5px] text-ink-faint">System role</span>}
            {isFounder && !r.is_system && (
              <InlineConfirm
                onConfirm={() => handleDelete(r.key)}
                label="Delete"
                confirmLabel={(assignedCounts[r.key] ?? 0) > 0 ? "Still assigned — can't delete" : 'Click again to confirm'}
                disabled={(assignedCounts[r.key] ?? 0) > 0}
              />
            )}
          </div>
        ))}
      </CardBody>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="New Role"
        icon={<KeyRound className="w-3.5 h-3.5" />}
        busy={busy}
        footer={
          <>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setModalOpen(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" form="new-role-form" className="flex-1" loading={busy}>Create</Button>
          </>
        }
      >
        <form id="new-role-form" onSubmit={handleCreate} className="space-y-3.5">
          <Field label="Label (shown everywhere)">
            <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Inventory Manager" />
          </Field>
          <Field label="Key (lowercase, no spaces — can't be changed later)">
            <Input required value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="e.g. inventory_manager" />
          </Field>
          <Field label="Badge Color">
            <Select value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value as (typeof TONE_OPTIONS)[number] })}>
              {TONE_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </Field>
          <p className="text-[11px] text-ink-faint">After creating, go to Role Permissions to pick which pages this role can see, then assign it to someone under Admin Users.</p>
        </form>
      </Modal>
    </Card>
  );
}
